import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from './types';
import { isTokenExpiringSoon, isTokenValid, clearTokensAndRedirect } from '../utils/jwt';

const api = axios.create({
  baseURL: `/api`,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

let accessToken: string | null = null;

// Token refresh lock mechanism to prevent race conditions
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Refresh the access token with race condition protection
 * Only one refresh request will be made at a time
 */
const refreshAccessToken = async (): Promise<string> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await axios.post<{ accessToken: string }>('/api/auth/refresh', {
        refreshToken
      }, {
        timeout: 10000,
      });

      accessToken = data.accessToken;
      localStorage.setItem('accessToken', data.accessToken);
      
      return data.accessToken;
    } catch (err) {
      // Clear tokens and redirect on refresh failure (including timeouts) so a
      // stuck/invalid token state can't survive a page reload and keep hanging.
      clearTokensAndRedirect();
      throw err;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Axios request interceptor: Attach access token to headers and proactively refresh if needed
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token refresh for auth endpoints
    if (config.url?.includes('/auth/refresh') || config.url?.includes('/auth/logout')) {
      if (!accessToken) {
        accessToken = localStorage.getItem('accessToken');
      }
      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    }

    if (!accessToken) {
      accessToken = localStorage.getItem('accessToken');
    }

    // If token is invalid/expired or expiring soon (< 5 minutes), try to refresh it
    // using the refresh token before giving up and logging the user out.
    if (accessToken && (!isTokenValid(accessToken) || isTokenExpiringSoon(accessToken, 5))) {
      console.log('Token invalid, expired, or expiring soon, attempting refresh');
      try {
        accessToken = await refreshAccessToken();
      } catch (err) {
        console.error('Failed to refresh token:', err);
        // clearTokensAndRedirect already called inside refreshAccessToken on failure
        return Promise.reject(err);
      }
    }

    if (accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    console.error('Request interceptor - Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401/403 errors with token refresh retry
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    console.error('Response interceptor - Error:', {
      status: error.response?.status,
      data: error.response?.data,
      config: error.config
    });
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status || 500;

    // Handle 401/403 errors with token refresh
    if ([401, 403].includes(status) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return api(originalRequest);
      } catch (err) {
        console.error('Failed to refresh token after 401/403:', err);
        // clearTokensAndRedirect already called in refreshAccessToken
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
